import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  ApiV1FindingService,
  EvidenceStatus,
  FindingStatus,
  IntakeStatus,
  ProvidedEvidenceIntake,
  RequirementIntake,
  SaveVerdictRequest,
  VerdictResult,
} from 'api';
import { TranslateModule } from '@ngx-translate/core';
import { catchError, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';
import { DatePipe } from '@angular/common';
import { AiGeneratedContentDirective } from 'common/disclaimer/disclaimer.directive';
import { AutofocusDirective } from 'common/form/autofocus.directive';
import { ButtonDirective } from 'common/button/button.directive';

interface OpenFindingsState {
  readonly isLoading: boolean;
  readonly hasError: boolean;
  readonly openCount: number;
}

interface WarningMessage {
  readonly key: string;
  readonly params?: Record<string, number | string>;
}

@Component({
  selector: 'comp-verdict-widget',
  templateUrl: './verdict-widget.component.html',
  styleUrls: ['./verdict-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    DatePipe,
    AiGeneratedContentDirective,
    AutofocusDirective,
    ButtonDirective,
  ],
})
export class VerdictWidgetComponent {
  private readonly findingService = inject(ApiV1FindingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly requirement = input.required<RequirementIntake>();
  readonly isSubmitPending = input<boolean>(false);
  readonly actionMessage = input<string | undefined>(undefined);

  evidencesWithQuestions = computed<ProvidedEvidenceIntake[]>(() =>
    this.requirement().evidences.filter((evidence) => evidence.questions.length > 0),
  );

  readonly verdictSaveRequested = output<{
    requirementId: number;
    command: SaveVerdictRequest;
  }>();

  protected readonly hydratedForRequirementId = signal<number | undefined>(undefined);
  protected readonly selectedResult = signal<VerdictResult | undefined>(undefined);
  protected readonly justification = signal('');
  protected readonly openFindingsState = signal<OpenFindingsState>({
    isLoading: false,
    hasError: false,
    openCount: 0,
  });

  protected readonly proposedResult = computed(() =>
    this.toVerdictResult(this.requirement().verdict?.proposedResult),
  );

  protected readonly proposalDate = computed(() => {
    return this.requirement().verdict?.updatedAt;
  });

  protected readonly savedResult = computed(() =>
    this.toVerdictResult(this.requirement().verdict?.finalResult),
  );

  protected readonly savedJustification = computed(
    () => this.requirement().verdict?.justification?.trim() ?? '',
  );

  protected readonly currentStatus = computed(
    () => this.requirement().intakeStatus ?? IntakeStatus.Incomplete,
  );

  protected readonly showProposedVerdict = computed(() => {
    return this.proposedResult() !== undefined;
  });

  protected readonly overrideRequiresJustification = computed(() => {
    const selectedResult = this.selectedResult();
    const proposedResult = this.proposedResult();

    return (
      selectedResult !== undefined &&
      proposedResult !== undefined &&
      selectedResult !== proposedResult
    );
  });

  protected readonly isJustificationMissing = computed(() => {
    return this.overrideRequiresJustification() && this.justification().trim().length === 0;
  });

  protected readonly warnings = computed<ReadonlyArray<WarningMessage>>(() => {
    const warnings: WarningMessage[] = [];
    const requirement = this.requirement();
    const status = this.currentStatus();
    const hasOpenFindings =
      !this.openFindingsState().hasError && this.openFindingsState().openCount > 0;

    if (
      requirement.evidences.some(
        (evidence) =>
          evidence.status === EvidenceStatus.Missing ||
          evidence.status === EvidenceStatus.NotProvided,
      )
    ) {
      warnings.push({ key: 'requirementIntake.verdict.warning.evidenceMissing' });
    }

    if (status === IntakeStatus.Incomplete || status === IntakeStatus.PendingAnalysis) {
      warnings.push({ key: 'requirementIntake.verdict.warning.analysisIncomplete' });
    }

    if (hasOpenFindings) {
      warnings.push({
        key: 'requirementIntake.verdict.warning.findingsOpen',
        params: { count: this.openFindingsState().openCount },
      });
    }

    return warnings;
  });

  protected readonly isSaveDisabled = computed(() => {
    return (
      this.isSubmitPending() ||
      this.selectedResult() === undefined ||
      this.isJustificationMissing() ||
      (this.requirement().verdict?.decided &&
        this.selectedResult() === this.savedResult() &&
        this.justification().trim() === this.savedJustification())
    );
  });

  protected readonly surfaceClass = computed(() => {
    switch (this.selectedResult()) {
      case VerdictResult.Pass:
        return 'verdict-surface verdict-surface-pass';
      case VerdictResult.Fail:
        return 'verdict-surface verdict-surface-fail';
      default:
        return 'verdict-surface';
    }
  });

  constructor() {
    effect(() => {
      const requirement = this.requirement();

      if (this.hydratedForRequirementId() === requirement.id) {
        return;
      }

      this.selectedResult.set(this.toVerdictResult(requirement.verdict?.finalResult));
      this.justification.set(requirement.verdict?.justification ?? '');
      this.hydratedForRequirementId.set(requirement.id);
    });

    toObservable(this.requirement)
      .pipe(
        distinctUntilChanged(),
        tap(() => {
          this.openFindingsState.set({
            isLoading: true,
            hasError: false,
            openCount: 0,
          });
        }),
        switchMap((requirement) =>
          this.findingService.getFindingsForRequirementApplication(requirement.id, 0, 250).pipe(
            map((result) => ({
              hasError: false,
              openCount: (result.items ?? []).filter(
                (finding) => finding.status === FindingStatus.Unresolved,
              ).length,
            })),
            catchError(() =>
              of({
                hasError: true,
                openCount: 0,
              }),
            ),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result) => {
        this.openFindingsState.set({
          isLoading: false,
          hasError: result.hasError,
          openCount: result.openCount,
        });
      });
  }

  protected selectResult(result: VerdictResult): void {
    this.selectedResult.set(result);
  }

  protected onJustificationInput(event: Event): void {
    const target = event.target;

    if (!(target instanceof HTMLTextAreaElement)) {
      return;
    }

    this.justification.set(target.value);
  }

  protected saveVerdict(): void {
    const selectedResult = this.selectedResult();
    if (selectedResult === undefined || this.isSaveDisabled()) {
      return;
    }

    const trimmedJustification = this.justification().trim();
    const command: SaveVerdictRequest = {
      result: selectedResult,
      justification: trimmedJustification.length > 0 ? trimmedJustification : undefined,
    };

    this.verdictSaveRequested.emit({
      requirementId: this.requirement().id,
      command,
    });
  }

  protected resultLabelKey(result: VerdictResult | undefined): string {
    switch (result) {
      case VerdictResult.Pass:
        return 'requirementIntake.verdict.result.pass';
      case VerdictResult.Fail:
        return 'requirementIntake.verdict.result.fail';
      default:
        return 'requirementIntake.verdict.result.unknown';
    }
  }

  protected resultBadgeClass(result: VerdictResult | undefined): string {
    switch (result) {
      case VerdictResult.Pass:
        return 'result-badge result-badge-pass';
      case VerdictResult.Fail:
        return 'result-badge result-badge-fail';
      default:
        return 'result-badge';
    }
  }

  private toVerdictResult(value: string | undefined): VerdictResult | undefined {
    if (value === VerdictResult.Pass) {
      return VerdictResult.Pass;
    }

    if (value === VerdictResult.Fail) {
      return VerdictResult.Fail;
    }

    return undefined;
  }
}
